import styled, { css } from 'styled-components';
import { BreakPoints } from '@makemydeal/dr-ui-bricks-utils';
import { Heading, Text } from '@makemydeal/dr-ui-bricks-components';

export const LeftNavContainer = styled.nav<{ $isOpen?: boolean }>`
    ${({ theme, $isOpen }) => css`
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 276px;
        min-width: 276px;
        height: 100%;
        padding: 16px;
        background-color: ${theme.uiBricks?.modalBackgroundColor};
        border-right: 1px solid ${theme.uiBricks?.modalWrapperBorderColor};
        overflow: auto;

        /* Slimmer scrollbar (Firefox + WebKit): default classic scrollbars on Windows render
           much wider than macOS overlay scrollbars, making the left nav look inconsistent
           across platforms. */
        scrollbar-width: thin;
        &::-webkit-scrollbar {
            width: 6px;
        }

        @media (max-width: ${BreakPoints.TABLET}) {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 1100;
            width: 276px;
            min-width: 276px;
            height: 100%;
            border-right: none;
            box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
            transform: translateX(${$isOpen ? '0' : '-100%'});
            transition: transform 0.25s ease-in-out;
            overflow: auto;
        }

        @media (max-width: ${BreakPoints.PHONE}) {
            width: 100%;
            gap: 8px;
            padding: 24px;
            overflow: auto;
        }
    `}
`;

export const DrawerBackdrop = styled.div<{ $isOpen?: boolean }>`
    display: none;

    @media (max-width: ${BreakPoints.TABLET}) {
        display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
        position: fixed;
        inset: 0;
        z-index: 1000;
        background-color: rgba(0, 0, 0, 0.5);
    }
`;

export const DrawerCloseButton = styled.button`
        display: none;

        /* Only shown on tablet widths where the nav is a drawer */
        @media (max-width: ${BreakPoints.TABLET}) {
            display: inline-flex;
            justify-content: center;
            position: absolute;
            top: 20px;
            right: 15px;
            width: 32px;
            height: 32px;
            padding: 0;
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 30px;
            line-height: 1;
            color: #999999};
        }
`;

export const NavSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

export const SectionDivider = styled.hr`
    ${({ theme }) => css`
        width: 100%;
        margin: 8px 0;
        border: none;
        border-top: 1px solid ${theme.uiBricks?.primaryBorderColor};
    `}
`;

export const SectionTitle = styled(Heading)`
    ${({ theme }) => css`
        color: ${theme.uiBricks?.labelFontColor};
    `}
`;

export const NavItemButton = styled.button<{ $isActive?: boolean; $isDisabled?: boolean }>`
    ${({ theme, $isActive, $isDisabled }) => css`
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-areas:
            'icon title'
            '. description';
        align-items: normal;
        column-gap: 8px;
        width: 100%;
        padding: 16px;
        border: ${$isActive ? `2px solid ${theme.uiBricks?.primaryAccentColor}` : '1px solid #B3B4B7'};
        border-radius: 8px;
        cursor: pointer;
        text-align: left;
        background-color: ${$isActive
            ? theme.uiBricks?.miniVdpGradientBottomColor
            : theme.uiBricks?.primaryBackground || 'transparent'};
        box-shadow: ${$isActive ? '0px 0px 12px 0px #00000033' : 'none'};
        transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;

        &:hover {
            background-color: ${$isActive ? theme.uiBricks?.miniVdpGradientBottomColor : '#f4f5f7'};
        }

        ${$isDisabled &&
        css`
            cursor: not-allowed;
            opacity: 0.5;
            box-shadow: none;
            background-color: transparent;

            &:hover {
                background-color: transparent;
            }
        `}
    `}
`;

export const NavItemIcon = styled.span<{ $isActive?: boolean }>`
    ${({ theme, $isActive }) => css`
        grid-area: icon;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 10px;
        background-color: ${$isActive ? theme.uiBricks?.primaryAccentColor : theme.uiBricks?.primaryBorderColor};
        color: ${$isActive ? theme.uiBricks?.modalBackgroundColor : theme.uiBricks?.defaultOpenModalBackground};

        svg {
            width: 24px;
            height: 24px;
        }

        ${$isActive &&
        css`
            svg path {
                fill: ${theme.uiBricks?.modalBackgroundColor};
            }
        `}
    `}
`;

export const NavItemText = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
`;

export const NavItemLabel = styled(Heading)<{ $isActive?: boolean }>`
    ${({ theme, $isActive }) => css`
        grid-area: title;
        color: ${$isActive ? theme.uiBricks?.primaryAccentColor : '#000000'};
    `}
`;

export const NavItemDescription = styled(Text)`
    ${() => css`
        grid-area: description;
        font-size: 14px;
        color: #54565a;
    `}
`;

export const NavItemWithTooltip = styled.div`
    ${({ theme }) => css`
        position: relative;
        display: flex;
        align-items: center;
        gap: 6px;
        width: 100%;

        /* Nav control fills the row and may shrink; the tooltip trigger keeps its size. */
        & > *:first-child {
            flex: 1 1 auto;
            min-width: 0;
        }

        /* ui-bricks Tooltip root (info icon + message). Anchor the popup to it. */
        & > *:last-child {
            flex: 0 0 auto;
            position: relative;
            display: inline-flex;
            align-items: center;
            cursor: help;
        }

        /*
         * The ui-bricks message element ("tooltip-text-wrapper"). Its own styles are
         * theme-driven and don't get injected in this app context (it would otherwise
         * render as raw inline text), so we style it here as a hover popup. It opens
         * above and toward the sidebar so it isn't clipped by the nav's overflow.
         */
        & .tooltip-text-wrapper {
            visibility: hidden;
            opacity: 0;
            position: absolute;
            right: 0;
            z-index: 1000;
            width: max-content;
            max-width: 220px;
            background-color: ${theme.uiBricks?.defaultOpenModalBackground};
            color: ${theme.uiBricks?.modalBackgroundColor};
            border-radius: 6px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
            transition: opacity 0.15s ease-in-out;
            pointer-events: none;
            transform: translateX(-100%) !important;
        }

        /* Hide the ui-bricks arrow pseudo-element; it's positioned for their layout. */
        & .tooltip-text-wrapper::after {
            display: none;
        }

        &:hover .tooltip-text-wrapper {
            visibility: visible;
            opacity: 1;
        }
    `}
`;

export const NavLinkItem = styled(Text)<{ $isDisabled?: boolean }>`
    ${({ theme, $isDisabled }) => css`
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
        text-align: left;
        font-size: 14px;
        font-weight: 600;
        color: ${theme.uiBricks?.primaryAccentColor};
        &:hover {
            text-decoration: none;
        }

        ${$isDisabled &&
        css`
            cursor: not-allowed;
            opacity: 0.5;

            &:hover {
                text-decoration: none;
            }
        `}
    `}
`;
